import React, { useEffect, useMemo, useState } from 'react';
import { LogEntry, SeveridadeLog } from '../models/logs';
import { useMaestroContext } from '../context/MaestroContext';
import maestroService from '../maestroService';
import { formatarData } from '../utils/formatters';
import { LABEL_AREA } from '../utils/labels';
import Card from '../components/Card';
import { EstadoSemPermissao } from '../components/EstadoErro';
import EstadoVazio from '../components/EstadoVazio';
import Header from '../components/Header';
import { SkeletonCard } from '../components/Skeletons';

const TODOS = 'TODOS';

const ORIGEM_LABEL: Record<string, string> = {
  ATIVIDADE_HISTORICO: 'Atividade',
  NOTIFICACAO: 'Notificação',
  SOD_DECISION: 'Segregação de funções',
};

// Central de logs — combina histórico de atividade, entregas de notificação e decisões de SoD do
// ciclo ativo (GET /api/maestro/ciclos/{id}/logs), todas trilhas append-only reais no backend.
const MaestroLogsPage: React.FC = () => {
  const { cicloAtivo, atividades, areas, empresas, usuarios, getArea, getEmpresa, getUsuario } = useMaestroContext();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  const [usuarioId, setUsuarioId] = useState<string>(TODOS);
  const [empresaId, setEmpresaId] = useState<string>(TODOS);
  const [areaId, setAreaId] = useState<string>(TODOS);
  const [origem, setOrigem] = useState<string>(TODOS);
  const [severidade, setSeveridade] = useState<string>(TODOS);
  const [simularSemPermissao, setSimularSemPermissao] = useState(false);

  useEffect(() => {
    if (!cicloAtivo.id) { setLogs([]); setCarregando(false); return; }
    let cancelado = false;

    maestroService.listarLogsPorCiclo(cicloAtivo.id, atividades)
      .then(dados => { if (!cancelado) setLogs(dados); })
      .catch(e => { if (!cancelado) setErro(e?.message ?? 'Não foi possível carregar os logs do ciclo.'); })
      .finally(() => { if (!cancelado) setCarregando(false); });

    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cicloAtivo.id]);

  const severidades: SeveridadeLog[] = ['INFO', 'ATENCAO', 'CRITICA'];

  const filtrados = useMemo(() => logs.filter(l =>
    (usuarioId === TODOS || l.usuarioId === Number(usuarioId)) &&
    (empresaId === TODOS || l.empresaId === Number(empresaId)) &&
    (areaId === TODOS || l.areaId === Number(areaId)) &&
    (origem === TODOS || l.origem === origem) &&
    (severidade === TODOS || l.severidade === severidade)
  ).sort((a, b) => b.criadoEm.localeCompare(a.criadoEm)), [logs, usuarioId, empresaId, areaId, origem, severidade]);

  // Exportação real (CSV client-side, sem depender de endpoint próprio no backend) — sempre reflete
  // exatamente os filtros aplicados na tela, igual à tabela visível.
  const exportarCsv = () => {
    const cabecalho = ['Data/Hora', 'Usuário', 'Origem', 'Ação', 'Empresa', 'Área', 'Detalhe', 'Severidade'];
    const escapar = (v: string) => `"${v.replace(/"/g, '""')}"`;
    const linhas = filtrados.map(log => {
      const usuario = getUsuario(log.usuarioId);
      const empresa = getEmpresa(log.empresaId);
      const area = getArea(log.areaId);
      const detalhe = log.statusAnterior && log.statusNovo ? `${log.statusAnterior} -> ${log.statusNovo}` : (log.detalhe ?? '');
      return [
        formatarData(log.criadoEm), usuario?.nome ?? `#${log.usuarioId}`, ORIGEM_LABEL[log.origem] ?? log.origem,
        log.acao.replace(/_/g, ' ') + (log.atividadeCodigo ? ` (${log.atividadeCodigo})` : ''),
        empresa?.nome ?? '', area ? LABEL_AREA[area.tipo] : '', detalhe, log.severidade,
      ].map(escapar).join(',');
    });
    const csv = '﻿' + [cabecalho.map(escapar).join(','), ...linhas].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `maestro-logs-ciclo-${cicloAtivo.id}-${cicloAtivo.competencia.replace('/', '-')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (carregando) return <div className='mc-page'><Header title='Central de Logs' /><SkeletonCard /></div>;
  if (erro) return <div className='mc-page'><Header title='Central de Logs' /><p className='mc-texto-danger'>{erro}</p></div>;

  return (
    <div className='mc-page'>
      <Header
        title='Central de Logs (padrão SOX)'
        subtitle='Trilha de auditoria real do ciclo: histórico de atividade, notificações e decisões de segregação de funções, com valores anterior/posterior quando aplicável.'
        actions={<button type='button' className='mc-btn mc-btn--secondary' disabled={filtrados.length === 0} onClick={exportarCsv}><i className='bi bi-download' /> Exportar CSV</button>}
      />

      <Card>
        <div className='mc-logs-filtros'>
          <label>Usuário
            <select value={usuarioId} onChange={e => setUsuarioId(e.target.value)}>
              <option value={TODOS}>Todos</option>
              {usuarios.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
            </select>
          </label>
          <label>Empresa
            <select value={empresaId} onChange={e => setEmpresaId(e.target.value)}>
              <option value={TODOS}>Todas</option>
              {empresas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </select>
          </label>
          <label>Área
            <select value={areaId} onChange={e => setAreaId(e.target.value)}>
              <option value={TODOS}>Todas</option>
              {areas.map(a => <option key={a.id} value={a.id}>{LABEL_AREA[a.tipo]}</option>)}
            </select>
          </label>
          <label>Origem
            <select value={origem} onChange={e => setOrigem(e.target.value)}>
              <option value={TODOS}>Todas</option>
              {Object.entries(ORIGEM_LABEL).map(([valor, rotulo]) => <option key={valor} value={valor}>{rotulo}</option>)}
            </select>
          </label>
          <label>Severidade
            <select value={severidade} onChange={e => setSeveridade(e.target.value)}>
              <option value={TODOS}>Todas</option>
              {severidades.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>
      </Card>

      <Card title={`${filtrados.length} registro(s)`}>
        {filtrados.length === 0
          ? <EstadoVazio titulo='Nenhum log encontrado para os filtros aplicados' />
          : (
            <table className='mc-logs-table'>
              <thead>
                <tr><th>Data/Hora</th><th>Usuário</th><th>Origem</th><th>Ação</th><th>Empresa</th><th>Área</th><th>Detalhe</th><th>Severidade</th></tr>
              </thead>
              <tbody>
                {filtrados.map((log, index) => {
                  const usuario = getUsuario(log.usuarioId);
                  const empresa = getEmpresa(log.empresaId);
                  const area = getArea(log.areaId);
                  return (
                    <tr key={`${log.origem}-${log.atividadeId ?? 0}-${index}`}>
                      <td>{formatarData(log.criadoEm)}</td>
                      <td>{usuario?.nome ?? `#${log.usuarioId}`}</td>
                      <td>{ORIGEM_LABEL[log.origem] ?? log.origem}</td>
                      <td>{log.acao.replace(/_/g, ' ')}{log.atividadeCodigo ? ` (${log.atividadeCodigo})` : ''}</td>
                      <td>{empresa?.nome ?? '—'}</td>
                      <td>{area ? LABEL_AREA[area.tipo] : '—'}</td>
                      <td>
                        {log.statusAnterior && log.statusNovo
                          ? <span>{log.statusAnterior} → {log.statusNovo}</span>
                          : (log.detalhe ?? '—')}
                      </td>
                      <td><span className={`mc-badge mc-badge--${log.severidade === 'CRITICA' ? 'danger' : log.severidade === 'ATENCAO' ? 'warning' : 'neutro'}`}>{log.severidade}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
      </Card>

      <Card title='Exportação auditável' subtitle='Recurso restrito a perfis de Auditoria e Compliance.'>
        <label className='mc-checkbox'>
          <input type='checkbox' checked={simularSemPermissao} onChange={e => setSimularSemPermissao(e.target.checked)} />
          Simular acesso sem permissão (demonstração do estado obrigatório)
        </label>
        {simularSemPermissao
          ? <EstadoSemPermissao recurso='Exportação de logs auditáveis' />
          : <p className='mc-texto-neutro'>Seu perfil tem acesso a este recurso — use o botão "Exportar CSV" no topo da página.</p>}
      </Card>
    </div>
  );
};

export default MaestroLogsPage;
